import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, MapPin, Users, Star, ThumbsUp, MessageCircle, Share2, Flag, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { getEvent, upvoteEvent, joinEventChat, getEventRatings, createRating } from '../lib/api';
import { formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingDialog, setRatingDialog] = useState(false);
  const [newRating, setNewRating] = useState({ rating: 5, review_text: '' });

  useEffect(() => {
    loadData();
  }, [eventId]);

  const loadData = async () => {
    try {
      const [eventRes, ratingsRes] = await Promise.all([
        getEvent(eventId),
        getEventRatings(eventId)
      ]);
      setEvent(eventRes.data);
      setRatings(ratingsRes.data);
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/feed');
    }
    setLoading(false);
  };

  const handleUpvote = async () => {
    try {
      const res = await upvoteEvent(eventId);
      setEvent(prev => ({ ...prev, upvotes: res.data.upvotes }));
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const handleJoin = async () => {
    try {
      await joinEventChat(eventId);
      toast.success('Joined chat!');
      navigate(`/chat/${eventId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleSubmitRating = async () => {
    try {
      await createRating({
        event_id: eventId,
        rating: newRating.rating,
        review_text: newRating.review_text || null
      });
      toast.success('Review submitted!');
      setRatingDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  if (loading || !event) {
    return (
      <div className="min-h-screen bg-transparent">
        <div className="animate-pulse text-muted-foreground">Loading event...</div>
      </div>
    );
  }

  const isOrganizer = event.organizer_id === user?.user_id;
  const isPastEvent = new Date(event.event_date) < new Date();

  return (
    <div className="min-h-screen bg-transparent">
      {/* Image Header */}
      <div className="relative h-72">
        <img
          src={event.image_url}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        
        {/* Back button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 bg-background/50 backdrop-blur-sm"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        {/* Badges */}
        <div className="absolute top-4 right-4 flex gap-2">
          {event.is_trending && (
            <Badge className="bg-secondary text-white">🔥 Trending</Badge>
          )}
          {event.is_women_only && (
            <Badge variant="outline" className="bg-background/50 backdrop-blur-sm">Women Only</Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 space-y-6 -mt-12 relative"
      >
        {/* Category & Title */}
        <div>
          <Badge variant="secondary" className="mb-2">{event.category}</Badge>
          <h1 className="text-3xl font-black tracking-tight">{event.title}</h1>
        </div>

        {/* Organizer */}
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarFallback className="bg-primary text-primary-foreground">
              {event.organizer_initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">Organized by {event.organizer_initials}</p>
            <p className="text-sm text-muted-foreground">
              {event.average_rating > 0 && (
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  {event.average_rating.toFixed(1)} rating
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Quick Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
            <Calendar className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Date & Time</p>
              <p className="font-medium">{formatDate(event.event_date)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
            <Clock className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Duration</p>
              <p className="font-medium">{event.duration_hours} hour{event.duration_hours > 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
            <MapPin className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Location</p>
              <p className="font-medium">{event.location}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
            <DollarSign className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Entry Fee</p>
              <p className="font-medium">{event.fee > 0 ? `£${event.fee.toFixed(2)}` : 'Free'}</p>
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <h2 className="font-semibold mb-2">About</h2>
          <p className="text-muted-foreground">{event.description}</p>
          {event.fee > 0 && (
            <p className="text-sm text-muted-foreground mt-2 italic">
              Note: Payments are direct P2P. Platform not involved.
            </p>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <ThumbsUp className="w-5 h-5 text-muted-foreground" />
            <span className="font-semibold">{event.upvotes}</span>
            <span className="text-muted-foreground">upvotes</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-muted-foreground" />
            <span className="font-semibold">{event.chat_members}</span>
            <span className="text-muted-foreground">joined</span>
          </div>
          {event.capacity && (
            <div className="text-muted-foreground">
              {event.capacity - event.chat_members} spots left
            </div>
          )}
        </div>

        <Separator />

        {/* Reviews */}
        {ratings.length > 0 && (
          <div>
            <h2 className="font-semibold mb-4">Reviews ({ratings.length})</h2>
            <div className="space-y-3">
              {ratings.slice(0, 5).map(r => (
                <div key={r.rating_id} className="p-3 rounded-xl bg-muted/30">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i < r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted'}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">— {r.user_initials}</span>
                  </div>
                  {r.review_text && <p className="text-sm">{r.review_text}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          {!isOrganizer && (
            <>
              <Button
                variant="outline"
                size="lg"
                className="flex-1"
                onClick={handleUpvote}
                data-testid="upvote-event-btn"
              >
                <ThumbsUp className="w-5 h-5 mr-2" />
                Upvote
              </Button>
              <Button
                size="lg"
                className="flex-1"
                onClick={handleJoin}
                data-testid="join-chat-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Join Chat
              </Button>
            </>
          )}
          {isPastEvent && !isOrganizer && (
            <Button
              variant="secondary"
              size="lg"
              onClick={() => setRatingDialog(true)}
              data-testid="rate-event-btn"
            >
              <Star className="w-5 h-5 mr-2" />
              Rate
            </Button>
          )}
        </div>

        {isOrganizer && (
          <Button
            size="lg"
            className="w-full"
            onClick={() => navigate(`/chat/${eventId}`)}
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            View Chat
          </Button>
        )}
      </motion.div>

      {/* Rating Dialog */}
      <Dialog open={ratingDialog} onOpenChange={setRatingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rate this Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setNewRating(prev => ({ ...prev, rating: star }))}
                  className="p-1"
                >
                  <Star
                    className={`w-10 h-10 transition-all ${
                      star <= newRating.rating
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-muted hover:text-yellow-400'
                    }`}
                  />
                </button>
              ))}
            </div>
            <Textarea
              placeholder="Write a review (optional)"
              value={newRating.review_text}
              onChange={(e) => setNewRating(prev => ({ ...prev, review_text: e.target.value }))}
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground text-right">
              {newRating.review_text.length}/200
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitRating}>Submit Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
